// Hosted-meeting call view (plan 0002): LiveKit room + the client-side
// scribe (D13) + live attributed transcript + host moderation.
// Spec: docs/product-specs/hosted-meeting.md

import { html, LitElement } from "lit";
import {
  ConnectionState,
  LocalAudioTrack,
  LocalTrackPublication,
  LocalVideoTrack,
  RemoteParticipant,
  RemoteTrack,
  Room,
  RoomEvent,
  Track,
  createLocalAudioTrack,
} from "livekit-client";
import { Recorder } from "../capture/recorder";
import { ApiError, fetchSegments, fetchTimelineEvents, hosted } from "../lib/api";
import { applySpeaker, loadDevicePrefs } from "../lib/devices";
import { MeetingStream } from "../lib/sse";
import { navigate } from "../lib/store";
import type { Meeting, MeetingEvent, Participant, Segment } from "../lib/types";

export class MmCall extends LitElement {
  static override properties = {
    meetingId: { attribute: false },
    selfParticipantId: { attribute: false },
    guestToken: { attribute: false },
    meetingTitle: { attribute: false },
    isHost: { attribute: false },
  };

  meetingId = 0;
  selfParticipantId = 0;
  guestToken: string | null = null;
  meetingTitle = "";
  isHost = false;

  private room: Room | null = null;
  private micTrack: LocalAudioTrack | null = null;
  private recorder: Recorder | null = null;
  private stream: MeetingStream | null = null;
  private participants: Participant[] = [];
  /// LiveKit identities currently connected ("p<id>").
  private connected = new Set<string>();
  private speaking = new Set<string>();
  private segments: Segment[] = [];
  private timelineEvents: MeetingEvent[] = [];
  private joinCode: string | null = null;
  private muted = false;
  private ended = false;
  /// Pre-join lobby: pick devices before we connect WebRTC + the scribe.
  private inLobby = true;
  private error = "";
  private notice = "";
  private copied = false;

  // SSE health: when a proxy starves the stream, a silent poll keeps
  // the transcript + participant list moving (SSE stays primary).
  private lastSseAt = 0;
  private pollTimer: ReturnType<typeof setInterval> | null = null;

  // ---- screen share (plan 0003) ----
  /// Am I the one presenting?
  private sharing = false;
  /// Whoever is presenting right now (one at a time by UI policy).
  private activeShare: { identity: string; name: string } | null = null;
  private shareTrack: RemoteTrack | LocalVideoTrack | null = null;
  /// Magnification of the share stage (1 = fit). Zoom >1 enables pan.
  private shareZoom = 1;

  override createRenderRoot(): HTMLElement {
    return this;
  }

  override connectedCallback(): void {
    super.connectedCallback();
    // Don't auto-connect: show the device lobby first (joinCall → setup).
  }

  private joinCall(): void {
    this.inLobby = false;
    this.requestUpdate();
    void this.setup();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    void this.teardown(false);
  }

  private async setup(): Promise<void> {
    try {
      // Transcript + participant events over SSE.
      this.stream = new MeetingStream(
        this.meetingId,
        {
          segment: (s) => {
            this.lastSseAt = Date.now();
            this.mergeSegments([s]);
          },
          status: (m) => {
            this.lastSseAt = Date.now();
            if (m.state && ["summarizing", "complete", "failed"].includes(m.state)) {
              this.onMeetingEnded();
            }
            const meeting = m as Partial<Meeting>;
            if (meeting.join_code) this.joinCode = meeting.join_code;
            if (meeting.title) this.meetingTitle = meeting.title;
            this.requestUpdate();
          },
          participant: ({ event, participant }) => {
            this.lastSseAt = Date.now();
            this.upsertParticipant(participant);
            if (event === "removed" && participant.id === this.selfParticipantId) {
              this.error = "you were removed from this meeting by the host";
              void this.teardown(false);
            }
            if (
              (event === "mic-revoked" || event === "mic-restored") &&
              participant.id === this.selfParticipantId
            ) {
              this.recorder?.setPaused(participant.mic_revoked || this.muted);
              this.notice = participant.mic_revoked
                ? "the host disabled your microphone"
                : "the host restored your microphone";
            }
            this.requestUpdate();
          },
          providerError: (e) => {
            this.notice = `transcription: ${e.message}`;
            this.requestUpdate();
          },
          meetingEvent: (ev) => {
            this.lastSseAt = Date.now();
            if (!this.timelineEvents.some((x) => x.id === ev.id)) {
              this.timelineEvents = [...this.timelineEvents, ev];
              this.requestUpdate();
            }
          },
        },
        this.guestToken,
      );
      this.stream.start();
      this.lastSseAt = Date.now();

      // Bootstrap over REST so the transcript shows even if the SSE
      // replay is delayed/eaten by a proxy.
      this.mergeSegments(
        await fetchSegments(this.meetingId, null, this.guestToken),
      );
      this.timelineEvents = await fetchTimelineEvents(this.meetingId, this.guestToken);
      const list = await hosted.participants(this.meetingId, this.guestToken);
      this.participants = list.participants;
      this.startPollFallback();

      // Connect WebRTC and publish the mic.
      const rtc = await hosted.rtcToken(this.meetingId, this.guestToken);
      this.room = new Room();
      this.wireRoomEvents(this.room);
      await this.room.connect(rtc.url, rtc.token);
      const devicePrefs = loadDevicePrefs();
      this.micTrack = await createLocalAudioTrack({
        deviceId: devicePrefs.micId || undefined,
        echoCancellation: devicePrefs.echoCancellation,
        noiseSuppression: devicePrefs.noiseSuppression,
        autoGainControl: devicePrefs.autoGainControl,
      });
      await this.room.localParticipant.publishTrack(this.micTrack);

      // The scribe (D13): same mic stream feeds VAD → chunks → STT.
      // Anchor this recorder's sample clock to meeting time so all
      // speakers share one ordered timeline.
      const anchor = rtc.server_now_ms - rtc.meeting_started_ms;
      // Version-skew guard: an older backend omits the clock fields;
      // NaN here would 400 every chunk upload. Fall back to 0
      // (join-relative offsets) rather than poisoning the pipeline.
      const baseOffsetMs = Number.isFinite(anchor) ? Math.max(0, anchor) : 0;
      this.recorder = new Recorder(
        this.meetingId,
        {
          onLevel: () => {},
          onQueueDepth: () => {},
          onSystemEnded: () => {},
          onUploadError: (message) => {
            this.notice = message;
            this.requestUpdate();
          },
        },
        this.guestToken,
        baseOffsetMs,
      );
      const ms = new MediaStream([this.micTrack.mediaStreamTrack]);
      await this.recorder.startFromStream(ms);
      this.requestUpdate();
    } catch (e) {
      this.error =
        e instanceof ApiError
          ? e.message
          : e instanceof Error
            ? e.message
            : "failed to join the call";
      this.requestUpdate();
    }
  }

  private wireRoomEvents(room: Room): void {
    room
      .on(
        RoomEvent.TrackSubscribed,
        (track: RemoteTrack, _pub, participant: RemoteParticipant) => {
          if (track.source === Track.Source.ScreenShare) {
            this.activeShare = {
              identity: participant.identity,
              name: participant.name || participant.identity,
            };
            this.shareTrack = track;
            this.requestUpdate();
          } else if (track.kind === Track.Kind.Audio) {
            // Attach: creates an <audio> element playing the speaker.
            const el = track.attach();
            el.dataset.lk = "remote-audio";
            this.appendChild(el);
            // Route to the preferred output device (Settings → Devices).
            void applySpeaker(el as HTMLMediaElement);
          }
        },
      )
      .on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack) => {
        if (track === this.shareTrack) this.clearShare();
        track.detach().forEach((el) => el.remove());
      })
      // Local share lifecycle is event-driven so the browser's native
      // "Stop sharing" bar stays in sync with our button.
      .on(RoomEvent.LocalTrackPublished, (pub: LocalTrackPublication) => {
        if (pub.source === Track.Source.ScreenShare && pub.track) {
          this.sharing = true;
          this.activeShare = {
            identity: `p${this.selfParticipantId}`,
            name: "You",
          };
          this.shareTrack = pub.track as LocalVideoTrack;
          this.requestUpdate();
        }
      })
      .on(RoomEvent.LocalTrackUnpublished, (pub: LocalTrackPublication) => {
        if (pub.source === Track.Source.ScreenShare) {
          this.sharing = false;
          this.clearShare();
        }
      })
      .on(RoomEvent.ParticipantConnected, (p: RemoteParticipant) => {
        this.connected.add(p.identity);
        this.requestUpdate();
      })
      .on(RoomEvent.ParticipantDisconnected, (p: RemoteParticipant) => {
        this.connected.delete(p.identity);
        this.requestUpdate();
      })
      .on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
        this.speaking = new Set(speakers.map((s) => s.identity));
        this.requestUpdate();
      })
      .on(RoomEvent.Disconnected, () => {
        if (!this.ended) this.onMeetingEnded();
      })
      .on(RoomEvent.ConnectionStateChanged, (s) => {
        if (s === ConnectionState.Connected && this.room) {
          this.connected = new Set(
            Array.from(this.room.remoteParticipants.values()).map((p) => p.identity),
          );
          this.connected.add(this.room.localParticipant.identity);
          this.requestUpdate();
        }
      });
  }

  private upsertParticipant(p: Participant): void {
    const i = this.participants.findIndex((x) => x.id === p.id);
    if (i >= 0) this.participants = this.participants.map((x) => (x.id === p.id ? p : x));
    else this.participants = [...this.participants, p];
  }

  private mergeSegments(incoming: Segment[]): void {
    const seen = new Set(this.segments.map((s) => s.id));
    const fresh = incoming.filter((s) => !seen.has(s.id));
    if (fresh.length === 0) return;
    this.segments = [...this.segments, ...fresh].sort(
      (a, b) => a.start_ms - b.start_ms || a.id - b.id,
    );
    this.requestUpdate();
  }

  /// Every 8s: if SSE has been silent >10s, pull fresh segments +
  /// participants over REST. Invisible when SSE is healthy.
  private startPollFallback(): void {
    this.pollTimer = setInterval(() => {
      if (this.ended || !this.stream) return;
      if (Date.now() - this.lastSseAt < 10_000) return;
      const lastMs = this.segments.length
        ? Math.max(...this.segments.map((s) => s.start_ms))
        : null;
      void fetchSegments(this.meetingId, lastMs, this.guestToken)
        .then((segs) => this.mergeSegments(segs))
        .catch(() => {});
      void hosted
        .participants(this.meetingId, this.guestToken)
        .then((list) => {
          this.participants = list.participants;
          this.requestUpdate();
        })
        .catch(() => {});
    }, 8_000);
  }

  private onMeetingEnded(): void {
    this.ended = true;
    void this.teardown(false);
    this.requestUpdate();
  }

  private async teardown(leaveApi: boolean): Promise<void> {
    if (leaveApi) {
      try {
        await hosted.leave(this.meetingId, this.guestToken);
      } catch {
        /* leaving is best-effort */
      }
    }
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
    await this.recorder?.stop().catch(() => {});
    this.recorder = null;
    this.clearShare();
    // Remove remote-participant audio elements explicitly — an abrupt
    // disconnect may not fire TrackUnsubscribed for each one.
    this.querySelectorAll('[data-lk="remote-audio"]').forEach((el) => {
      (el as HTMLMediaElement).srcObject = null;
      el.remove();
    });
    this.micTrack?.stop();
    this.micTrack = null;
    await this.room?.disconnect().catch(() => {});
    this.room = null;
    this.stream?.stop();
    this.stream = null;
  }

  // ---- screen share (plan 0003) ----

  private clearShare(): void {
    this.shareTrack?.detach().forEach((el) => el.remove());
    this.shareTrack = null;
    this.activeShare = null;
    this.shareZoom = 1;
    this.requestUpdate();
  }

  private async toggleShare(): Promise<void> {
    if (!this.room) return;
    try {
      // Handles getDisplayMedia + publish/unpublish; state lands via
      // LocalTrackPublished/Unpublished events.
      await this.room.localParticipant.setScreenShareEnabled(!this.sharing);
    } catch {
      // User cancelled the share picker — not an error.
    }
  }

  private readonly ZOOM_MIN = 1;
  private readonly ZOOM_MAX = 4;
  private readonly ZOOM_STEP = 0.25;

  private zoomBy(delta: number): void {
    const z = Math.min(this.ZOOM_MAX, Math.max(this.ZOOM_MIN, this.shareZoom + delta));
    this.shareZoom = Math.round(z * 100) / 100;
    this.requestUpdate();
  }

  private zoomReset(): void {
    this.shareZoom = 1;
    this.requestUpdate();
  }

  /// Lit re-renders replace the stage div; (re)attach the video there
  /// and apply the current zoom (scaling width grows height in step,
  /// and the stage scrolls so the user can pan a magnified share).
  override updated(): void {
    const stage = this.querySelector<HTMLElement>("#share-stage");
    if (!stage) return;
    if (this.shareTrack && !stage.querySelector("video")) {
      stage.appendChild(this.shareTrack.attach());
    }
    const video = stage.querySelector<HTMLVideoElement>("video");
    if (video) {
      const zoomed = this.shareZoom > 1;
      video.style.width = `${this.shareZoom * 100}%`;
      video.style.maxHeight = zoomed ? "none" : "65vh";
      stage.style.overflow = zoomed ? "auto" : "hidden";
      stage.style.maxHeight = zoomed ? "80vh" : "";
    }
  }

  // ---- user actions ----

  private toggleMute(): void {
    this.muted = !this.muted;
    if (this.muted) void this.micTrack?.mute();
    else void this.micTrack?.unmute();
    const self = this.participants.find((p) => p.id === this.selfParticipantId);
    this.recorder?.setPaused(this.muted || (self?.mic_revoked ?? false));
    this.requestUpdate();
  }

  private async copyInvite(): Promise<void> {
    const code = this.joinCode;
    if (!code) return;
    const url = `${location.origin}/#/join/${code}`;
    await navigator.clipboard.writeText(url);
    this.copied = true;
    this.requestUpdate();
    setTimeout(() => {
      this.copied = false;
      this.requestUpdate();
    }, 1500);
  }

  private async resetInvite(): Promise<void> {
    const r = await hosted.resetCode(this.meetingId);
    this.joinCode = r.join_code;
    this.notice = "invite link reset — the old link no longer works";
    this.requestUpdate();
  }

  private async endMeeting(): Promise<void> {
    if (!confirm("End the meeting for everyone and generate the summary?")) return;
    try {
      await hosted.end(this.meetingId);
    } catch (e) {
      this.error = e instanceof ApiError ? e.message : "failed to end meeting";
      this.requestUpdate();
    }
  }

  private async leaveCall(): Promise<void> {
    await this.teardown(true);
    if (this.guestToken) {
      this.ended = true; // guests have nowhere else to go
      this.requestUpdate();
    } else {
      navigate(`/meetings/${this.meetingId}`);
    }
  }

  // ---- host moderation ----

  private async modMute(p: Participant): Promise<void> {
    await hosted.mute(this.meetingId, p.id, true).catch(() => {});
  }

  private async modMic(p: Participant): Promise<void> {
    await hosted
      .setMic(this.meetingId, p.id, p.mic_revoked) // toggle
      .catch(() => {});
  }

  private async modKick(p: Participant): Promise<void> {
    if (!confirm(`Remove ${p.display_name} from the meeting?`)) return;
    await hosted.kick(this.meetingId, p.id).catch(() => {});
  }

  // ---- render ----

  private participantRow(p: Participant) {
    if (p.state === "removed") return "";
    const online = this.connected.has(`p${p.id}`);
    const talking = this.speaking.has(`p${p.id}`);
    const self = p.id === this.selfParticipantId;
    return html`<div class="timeline-item">
      <div class="timeline-head">
        <span class="row">
          <span class=${talking ? "rec-dot" : "muted"}>${online ? "●" : "◌"}</span>
          <strong>${p.display_name}${self ? " (you)" : ""}</strong>
          ${p.role !== "member"
            ? html`<span class="badge badge-accent">${p.role}</span>`
            : ""}
          ${p.mic_revoked ? html`<span class="badge badge-warn">mic off</span>` : ""}
        </span>
        ${this.isHost && !self
          ? html`<span class="row">
              <button class="ghost-button" title="Mute (they can unmute)"
                @click=${() => this.modMute(p)}>🔇</button>
              <button class="ghost-button"
                title=${p.mic_revoked ? "Restore microphone" : "Revoke microphone"}
                @click=${() => this.modMic(p)}>${p.mic_revoked ? "🎙✓" : "🎙✕"}</button>
              <button class="danger-button" title="Remove from meeting"
                @click=${() => this.modKick(p)}>✕</button>
            </span>`
          : ""}
      </div>
    </div>`;
  }

  override render() {
    if (this.ended) {
      return html`<section class="summary">
        <p class="eyebrow">Hosted meeting</p>
        <h1>${this.meetingTitle || "Meeting"} has ended</h1>
        <p class="muted">
          ${this.guestToken
            ? "Thanks for joining! The host has the transcript and summary."
            : "Generating the summary — opening the meeting page…"}
        </p>
        ${this.error
          ? html`<div class="message-card message-card-warn"><p>${this.error}</p></div>`
          : ""}
        ${!this.guestToken
          ? html`<button class="primary-button"
              @click=${() => navigate(`/meetings/${this.meetingId}`)}>
              View transcript & summary
            </button>`
          : ""}
      </section>`;
    }
    if (this.inLobby) {
      return html`<section class="summary">
        <p class="eyebrow">Hosted meeting</p>
        <h1>${this.meetingTitle || "Meeting"}</h1>
        <p class="muted">Check your microphone and speaker, then join.</p>
        <mm-device-picker></mm-device-picker>
        <div class="toolbar" style="margin-top: 1rem">
          <button class="primary-button" @click=${this.joinCall}>🎙 Join call</button>
          ${this.guestToken
            ? ""
            : html`<button
                class="ghost-button"
                @click=${() => navigate(`/meetings/${this.meetingId}`)}
              >
                Cancel
              </button>`}
        </div>
      </section>`;
    }
    return html`
      <section class="summary">
        <p class="eyebrow">Hosted meeting · live</p>
        <div class="timeline-head">
          <h1>${this.meetingTitle || "Meeting"}</h1>
          <span class="row"><span class="rec-dot"></span> live</span>
        </div>
        <div class="toolbar">
          <button class=${this.muted ? "primary-button" : "ghost-button"}
            @click=${this.toggleMute}>
            ${this.muted ? "🔇 Unmute" : "🎙 Mute"}
          </button>
          <button class=${this.sharing ? "primary-button" : "ghost-button"}
            ?disabled=${!!this.activeShare && !this.sharing}
            title=${this.activeShare && !this.sharing
              ? `${this.activeShare.name} is presenting`
              : "Share a screen, window, or tab"}
            @click=${this.toggleShare}>
            ${this.sharing ? "⏹ Stop sharing" : "🖥 Share screen"}
          </button>
          ${this.isHost
            ? html`
                <button class="ghost-button" @click=${this.copyInvite}>
                  ${this.copied ? "✓ Copied" : "Copy invite link"}
                </button>
                <button class="ghost-button" @click=${this.resetInvite}>
                  Reset link
                </button>
                <span class="grow"></span>
                <button class="danger-button" @click=${this.endMeeting}>
                  ■ End meeting
                </button>
              `
            : html`<span class="grow"></span>
                <button class="ghost-button" @click=${this.leaveCall}>Leave</button>`}
        </div>
      </section>
      ${this.error
        ? html`<div class="message-card message-card-error"><p>${this.error}</p></div>`
        : ""}
      ${this.notice
        ? html`<div class="message-card message-card-info"><p>${this.notice}</p></div>`
        : ""}
      ${this.activeShare
        ? html`<section>
            <div class="timeline-head">
              <h2>
                ${this.sharing
                  ? "You are presenting"
                  : `${this.activeShare.name} is presenting`}
              </h2>
              <span class="row share-zoom">
                <button
                  class="ghost-button"
                  title="Zoom out"
                  ?disabled=${this.shareZoom <= this.ZOOM_MIN}
                  @click=${() => this.zoomBy(-this.ZOOM_STEP)}
                >
                  −
                </button>
                <span class="muted">${Math.round(this.shareZoom * 100)}%</span>
                <button
                  class="ghost-button"
                  title="Zoom in"
                  ?disabled=${this.shareZoom >= this.ZOOM_MAX}
                  @click=${() => this.zoomBy(this.ZOOM_STEP)}
                >
                  ＋
                </button>
                <button
                  class="ghost-button"
                  title="Fit to width"
                  ?disabled=${this.shareZoom === 1}
                  @click=${this.zoomReset}
                >
                  Fit
                </button>
              </span>
            </div>
            <div id="share-stage" class="share-stage"></div>
          </section>`
        : ""}
      <section>
        <h2>Participants</h2>
        <div class="timeline">
          ${this.participants.map((p) => this.participantRow(p))}
        </div>
      </section>
      <section>
        <h2>Live transcript</h2>
        <mm-transcript
          .segments=${this.segments}
          .markers=${this.timelineEvents}
        ></mm-transcript>
      </section>
    `;
  }
}

customElements.define("mm-call", MmCall);
